/**
 * Follower — Phaser wrapper around FollowerState (pure logic in
 * `followerState.ts`).
 *
 * Owns the Phaser sprite + tweens; observes FollowerState transitions and
 * drives the visual side effects:
 *   - step / hop tweens at the 140 ms / 280 ms cadence
 *   - directional frame rendering from a 9-frame HGSS-style sheet
 *     (idle cluster 0/1/2 = down/up/LEFT, walk pairs 3-4/5-6/7-8,
 *     flipX for right — the TaTaTaZJJ layout; see OW_IDLE_FRAME /
 *     OW_WALK_FRAMES below, which are the authoritative tables. NOT
 *     the row-per-direction layout an HGSS rip would suggest.)
 *   - walk-cycle frame alternation per step (left-foot / right-foot
 *     pattern sync'd to the player's step cadence)
 *   - send-out Pokéball-pop animation on spawn / map-load / lead-change
 *
 * Camera coupling: attached to `cameras.main` (default). Do NOT use
 * `setScrollFactor(0)` or attach to a UI camera — the follower lives in
 * world space and scrolls with the player.
 *
 * Texture loading: the Follower expects the WorldScene to have preloaded
 * each v0.1 species' overworld sheet at the key `follower-ow:<species>`
 * via `scene.load.spritesheet(..., { frameWidth: 32, frameHeight: 32 })`.
 * For species not preloaded (post-v0.1 roster expansion), the lazy-load
 * path queues Phaser's loader + waits for completion.
 *
 * Follower wrapper is INTEGRATION-tested via WorldScene-level tests, NOT
 * vitest. All 33 unit specs target FollowerState (the pure-logic class
 * this composes).
 */

import Phaser from 'phaser';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { audio } from '../systems/audio';
import {
  FollowerState,
  type Facing,
  type Tile,
  type FollowerSnapshot,
} from './followerState';

/** Peak lift of the follower's hop sin-arc, in world pixels above the
 *  straight-line lerp. Applied inside updateWalkProgress when walkKind
 *  === 'hop'. Step + hop are both progress-driven by the player's step,
 *  so total hop duration matches the player's current step cadence. */
const HOP_ARC_PX = 12;
const SEND_OUT_TOTAL_MS = 1000;
const SEND_OUT_STAGE_OLD_FADE_MS = 200;
const SEND_OUT_STAGE_POKEBALL_DURATION_MS = 100;
const SEND_OUT_STAGE_FLASH_DURATION_MS = 100;
const SEND_OUT_STAGE_EMERGE_DURATION_MS = 600;
const SEND_OUT_TIMER_POKEBALL_AT_MS = 200;
const SEND_OUT_TIMER_FLASH_AT_MS = 300;
const SEND_OUT_TIMER_EMERGE_AT_MS = 400;
const FOLLOWER_DEPTH = 4;

/** Scene event fired when materialize() creates a new OW sprite. The
 *  UI camera default-renders all world objects until explicitly told to
 *  ignore them via `cameras.ignore(obj)`. The pokeball + flash are
 *  added at construction so WorldScene's loadMap-time syncUiCameraIgnores
 *  catches them; the OW sprite is lazy (created on the first materialize),
 *  so without this event the sprite double-renders through the UI camera
 *  at screen (worldX, worldY) — a tiny viewport-anchored copy that stays
 *  in place as the world camera scrolls. WorldScene subscribes and re-
 *  syncs its ignore list. */
export const FOLLOWER_SPRITE_CREATED_EVENT = 'poke:follower-sprite-created';

/** Frame layout for the 9-frame TaTaTaZJJ overworld sheets. Verified
 *  empirically against the Bulbasaur sheet (sprite-fetch --mode=overworld
 *  baked PNGs at apps/game/public/assets/pokemon/follower/<id>.png):
 *
 *    Frame 0: down idle    Frame 3: down walk-A    Frame 4: down walk-B
 *    Frame 1: up idle      Frame 5: up walk-A      Frame 6: up walk-B
 *    Frame 2: LEFT idle    Frame 7: LEFT walk-A    Frame 8: LEFT walk-B
 *
 *  Two layout quirks vs the pokeemerald-canon player sheet:
 *    1. Idle frames cluster first (0-2), then walk pairs per direction
 *       (3-4 / 5-6 / 7-8). Player uses 0-2 down, 3-5 up, 6-8 right.
 *    2. Side sprite is drawn facing LEFT, not RIGHT. Player uses
 *       right-facing with flipX for left; TaTaTaZJJ inverts: left-
 *       facing with flipX for right. */
export const OW_IDLE_FRAME: Readonly<Record<'down' | 'up' | 'left', number>> = {
  down: 0,
  up: 1,
  left: 2,
};
export const OW_WALK_FRAMES: Readonly<Record<'down' | 'up' | 'left', [number, number]>> = {
  down: [3, 4],
  up: [5, 6],
  left: [7, 8],
};

/** Map any of the four cardinal facings onto the three encoded
 *  directions in the sheet (right mirrors left via flipX). Exported
 *  (with the frame tables above) for RoamerSystem — roam-mode wild
 *  sprites render from the same sheets with the same layout. */
export function encodedDirection(facing: Facing): 'down' | 'up' | 'left' {
  if (facing === 'left' || facing === 'right') return 'left';
  return facing;
}

export class Follower {
  private scene: Phaser.Scene;
  private tileSize: number;
  private state: FollowerState;
  private sprite?: Phaser.GameObjects.Sprite;
  private pokeball?: Phaser.GameObjects.Sprite;
  private flash?: Phaser.GameObjects.Arc;
  /** Step + hop walk state — populated on `onPlayerStepStart`; consumed
   *  each frame by `updateWalkProgress(t)`. Both kinds are progress-
   *  driven from WorldScene.advanceStep's elapsed/duration so the
   *  follower never drifts behind a parallel timing source — including
   *  the chain-step-after-ledge-hop case (Bug 6) where a 280ms Phaser
   *  hop tween would get cancelled mid-arc when the next 140ms step
   *  starts, causing a visible snap. */
  private walkActive = false;
  private walkKind: 'step' | 'hop' = 'step';
  private walkStartX = 0;
  private walkStartY = 0;
  private walkTargetX = 0;
  private walkTargetY = 0;
  private sendOutTweens: Phaser.Tweens.Tween[] = [];
  private sendOutTimers: Phaser.Time.TimerEvent[] = [];
  /** Lazy-load deduplication — species we've already triggered a load
   *  for. Lazy path only fires for non-preloaded species (post-v0.1
   *  expansion); v0.1's 12 species are all preloaded by WorldScene. */
  private lazyLoadStarted = new Set<string>();
  /** Step counter for walk-frame alternation. Increments on every
   *  `onPlayerStepStart`; mod 2 picks `walkFrames[0]` (left-foot) vs
   *  `walkFrames[1]` (right-foot). Never decrements; wraps naturally
   *  via modulo. */
  private stepCounter = 0;

  constructor(scene: Phaser.Scene, tileSize: number) {
    this.scene = scene;
    this.tileSize = tileSize;
    this.state = new FollowerState();

    this.pokeball = this.scene.add
      .sprite(0, 0, 'item_ball', 0)
      .setOrigin(0.5, 1.0)
      .setDepth(FOLLOWER_DEPTH + 1)
      .setVisible(false);
    this.flash = this.scene.add
      .circle(0, 0, this.tileSize * 0.7, 0xffffff, 0.9)
      .setDepth(FOLLOWER_DEPTH + 2)
      .setVisible(false);
  }

  get snapshot(): FollowerSnapshot {
    return this.state.snapshot();
  }

  get sendOutInProgress(): boolean {
    return this.state.sendOutInProgress;
  }

  getAllSprites(): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    if (this.sprite) out.push(this.sprite);
    if (this.pokeball) out.push(this.pokeball);
    if (this.flash) out.push(this.flash);
    return out;
  }

  // ─── Public lifecycle ────────────────────────────────────────────

  /** First-time follower appearance — positions + plays the Pokéball-
   *  pop animation. Per HGSS cartridge behavior, this fires only when
   *  the run actually gets a lead for the first time (starter pick).
   *  Boot resume, map transitions, and battle WAKE use `place()`
   *  instead so the follower is simply *already there* without
   *  re-summoning. */
  spawn(
    playerTile: Tile,
    facing: Facing,
    species: string,
    npcTiles?: ReadonlySet<string>,
    preferredTile?: Tile,
  ): void {
    this.state.spawn(playerTile, facing, species, npcTiles, preferredTile);
    void this.ensureLoaded(species).then(() => {
      this.materialize(species);
      this.applyVisibility();
      this.snapToHomeTile();
      this.beginSendOut();
    });
  }

  /** Is the follower currently out in the world (species set and either
   *  visible or pending its step-out reveal)? The cinematic-recall path
   *  uses this for idempotence — recalling a follower that isn't out is
   *  a no-op. */
  get isOut(): boolean {
    const snap = this.state.snapshot();
    return !!snap.species && (snap.visible || snap.pendingReveal);
  }

  /** HGSS convention (round-5): recall the follower into its ball for a
   *  cinematic. Full-motion: a quick shrink-and-fade (the send-out pop
   *  in reverse, ~280ms); instant=true (reduce-animations toggle)
   *  collapses to an immediate hide. State lands in the same shape as
   *  onSleep — species preserved, so the bracket-end spawn() replays
   *  the Pokéball-pop send-out to bring it back. */
  recall(instant: boolean): void {
    this.cancelWalkTween();
    this.cancelSendOut();
    if (!instant && this.sprite && this.sprite.visible) {
      const sprite = this.sprite;
      this.scene.tweens.add({
        targets: sprite,
        scale: 0,
        alpha: 0,
        duration: 280,
        ease: 'Back.easeIn',
        onComplete: () => {
          this.state.onSleep();
          this.applyVisibility();
          // Restore render props for the next materialize/spawn.
          sprite.setAlpha(1).setScale(1);
        },
      });
      return;
    }
    this.state.onSleep();
    this.applyVisibility();
  }

  /** Position the follower at its derived home tile WITHOUT animation.
   *  HGSS: once the lead is set, the Pokémon is "out" persistently —
   *  it's already beside you when you load into a new map / wake from
   *  a battle / resume from save. The ball animation is only for the
   *  act of selecting (starter pick + lead swap). */
  place(
    playerTile: Tile,
    facing: Facing,
    species: string,
    npcTiles?: ReadonlySet<string>,
  ): void {
    this.cancelWalkTween();
    this.cancelSendOut();
    this.state.spawn(playerTile, facing, species, npcTiles);
    // state.spawn sets sendOutInProgress=true via placeBehindOrShuffle;
    // clear it since no animation is playing — otherwise the
    // WorldScene's movement gate would lock the player out.
    this.state.completeSendOut();
    void this.ensureLoaded(species).then(() => {
      this.materialize(species);
      this.applyVisibility();
      this.snapToHomeTile();
      // Ensure full visibility in case a prior send-out left the
      // sprite at alpha=0/scale=0 mid-animation.
      if (this.sprite) this.sprite.setAlpha(1).setScale(1);
    });
  }

  onPlayerStepStart(
    oldTile: Tile,
    newTile: Tile,
    facing: Facing,
    tweenStartMs: number,
  ): void {
    const before = this.state.snapshot();
    this.state.onPlayerStepStart(oldTile, newTile, facing, tweenStartMs);
    const after = this.state.snapshot();

    if (before.pendingReveal && !after.pendingReveal) {
      this.applyVisibility();
      this.snapToHomeTile();
      this.updateSpriteFrame(); // freshly visible — show idle for facing
      return;
    }

    if (after.targetTile && after.tweenKind && after.tweenKind !== 'sendOut') {
      this.beginWalkTween(after.targetTile, after.tweenKind);
    }
  }

  onPlayerStepComplete(): void {
    this.state.onPlayerStepComplete();
    // Snap the sprite to its final target — guards against the
    // updateWalkProgress tick landing at t=0.97 instead of exactly
    // t=1.0 due to update-loop overshoot rounding.
    if (this.walkActive && this.sprite) {
      this.sprite.x = this.walkTargetX;
      this.sprite.y = this.walkTargetY;
      this.walkActive = false;
    }
    this.updateSpriteFrame();
  }

  onPlayerHopStart(oldTile: Tile, newTile: Tile): void {
    this.state.onPlayerHopStart(oldTile, newTile);
  }

  /** Player turned in place — face a wall, switch input source, etc.
   *  Update the follower's facing so its sprite frame stays in sync
   *  with the player. Pure cosmetic; no tween fires. */
  onPlayerFacingChange(facing: Facing): void {
    this.state.onPlayerFacingChange(facing);
    this.updateSpriteFrame();
  }

  onMapLoad(
    playerTile: Tile,
    facing: Facing,
    npcTiles?: ReadonlySet<string>,
  ): void {
    this.cancelWalkTween();
    this.cancelSendOut();
    this.state.onMapLoad(playerTile, facing, npcTiles);
    // HGSS: follower is "already there" on the new map — no
    // re-summoning animation. Clear the gate the state machine sets
    // via placeBehindOrShuffle so the WorldScene's movement gate
    // doesn't lock the player out.
    this.state.completeSendOut();
    const species = this.state.snapshot().species;
    if (!species) return;
    void this.ensureLoaded(species).then(() => {
      this.materialize(species);
      this.applyVisibility();
      this.snapToHomeTile();
      if (this.sprite) this.sprite.setAlpha(1).setScale(1);
    });
  }

  onSleep(): void {
    this.cancelWalkTween();
    this.cancelSendOut();
    this.state.onSleep();
    this.applyVisibility();
  }

  /** `preferredTile` (in-world duels) pins the follower back onto the
   *  tile it slept on instead of re-deriving from the player's facing —
   *  see FollowerState.onWake. */
  onWake(
    playerTile: Tile,
    facing: Facing,
    npcTiles?: ReadonlySet<string>,
    preferredTile?: Tile,
  ): void {
    this.cancelWalkTween();
    this.cancelSendOut();
    this.state.onWake(playerTile, facing, npcTiles, preferredTile);
    // HGSS: returning from battle (or any sleeping scene), the
    // follower is just there beside you — no Pokéball animation.
    this.state.completeSendOut();
    const species = this.state.snapshot().species;
    if (!species) {
      this.applyVisibility();
      return;
    }
    void this.ensureLoaded(species).then(() => {
      this.materialize(species);
      this.applyVisibility();
      this.snapToHomeTile();
      if (this.sprite) this.sprite.setAlpha(1).setScale(1);
    });
  }

  onDialogueOpen(): void {
    this.state.onDialogueOpen();
    // Step + hop are both driven by WorldScene.advanceStep — that loop
    // bails out when dialogueOpen is true, so the animation naturally
    // freezes without an explicit pause here.
  }

  onDialogueClose(): void {
    this.state.onDialogueClose();
  }

  onNpcTilesChange(npcTiles: ReadonlySet<string>): void {
    const before = this.state.snapshot();
    this.state.onNpcTilesChange(npcTiles);
    const after = this.state.snapshot();

    if (before.visible && !after.visible) {
      this.applyVisibility();
      return;
    }
    if (after.targetTile && !before.targetTile && after.tweenKind === 'step') {
      this.beginWalkTween(after.targetTile, 'step');
    }
  }

  onLeadChanged(species: string): void {
    this.cancelWalkTween();
    this.cancelSendOut();
    this.state.onLeadChanged(species);
    void this.ensureLoaded(species).then(() => {
      // DON'T materialize the new species yet. The send-out's stage 1
      // fades the CURRENT (old) sprite to alpha 0 over 200 ms; we
      // swap to the new texture at that fade's onComplete, when
      // alpha=0 so the swap is invisible. Without this, the new
      // species would flash visible briefly before the Pokéball pop,
      // looking like "new follower → ball → new follower again."
      this.beginSendOut(species);
    });
  }

  flushPending(): void {
    this.state.flushPending();
    this.cancelSendOut();
  }

  // ─── Internal helpers ─────────────────────────────────────────────

  /** Position the sprite on the follower's home tile. X = tile center;
   *  Y = tile bottom so the sprite's bottom edge — its visible ground
   *  line with origin (0.5, 1.0) — sits on the same ground line as
   *  the player. The player sprite (16×32, origin 0.5/0.75) renders
   *  with feet at tile bottom; matching here keeps both characters'
   *  ground lines aligned when they're on adjacent tiles. */
  private snapToHomeTile(): void {
    if (!this.sprite) return;
    const { tileX, tileY } = this.state.snapshot();
    this.sprite.setPosition(
      (tileX + 0.5) * this.tileSize,
      (tileY + 1.0) * this.tileSize,
    );
  }

  private applyVisibility(): void {
    if (!this.sprite) return;
    this.sprite.setVisible(this.state.snapshot().visible);
  }

  /** Create / reposition the sprite and apply the directional idle
   *  frame for the current facing. The texture is a 9-frame
   *  spritesheet preloaded by WorldScene; this just selects the
   *  right frame. */
  private materialize(species: string): void {
    const textureKey = `follower-ow:${species}`;

    if (!this.scene.textures.exists(textureKey)) {
      console.warn(`[follower] texture missing for ${species}`);
      return;
    }

    if (!this.sprite) {
      this.sprite = this.scene.add
        .sprite(0, 0, textureKey, 0)
        .setOrigin(0.5, 1.0)
        .setDepth(FOLLOWER_DEPTH);
      // Tell WorldScene the UI camera needs to add this sprite to its
      // ignore list — otherwise the lazy sprite double-renders through
      // the UI camera (zoom=1 + scroll=0) as a tiny viewport-anchored
      // copy of itself. See FOLLOWER_SPRITE_CREATED_EVENT docs.
      this.scene.events.emit(FOLLOWER_SPRITE_CREATED_EVENT);
    } else {
      this.sprite.setTexture(textureKey, 0);
    }

    // Set the directional idle frame for the current facing — the
    // post-spawn / post-map-load pose. updateSpriteFrame would do the
    // same; we call it directly for clarity.
    this.updateSpriteFrame();
  }

  /** Set the sprite frame + flipX based on the current facing and
   *  whether a step tween is in flight. Pure read-from-state; called
   *  on every transition that could change the visible frame. */
  private updateSpriteFrame(): void {
    if (!this.sprite) return;
    const s = this.state.snapshot();
    const facing = s.facing;
    const dir = encodedDirection(facing);
    // "Walking" = step or hop in flight (both progress-driven).
    const isWalking = this.walkActive;
    let frame: number;
    if (isWalking) {
      const pair = OW_WALK_FRAMES[dir];
      frame = (this.stepCounter % 2 === 0 ? pair[0] : pair[1]);
    } else {
      frame = OW_IDLE_FRAME[dir];
    }
    this.sprite.setFrame(frame);
    // Sheet's side sprite is drawn facing LEFT — flip for RIGHT.
    this.sprite.setFlipX(facing === 'right');
  }

  private beginWalkTween(target: Tile, kind: 'step' | 'hop'): void {
    if (!this.sprite) return;
    this.cancelWalkTween();

    // Snap to the logical home tile before computing start coords.
    // Eliminates any per-step fractional shortfall accumulating
    // across chained walks (was the rubber-band root cause when we
    // ran step as a parallel Phaser tween).
    const { tileX, tileY } = this.state.snapshot();
    this.sprite.x = (tileX + 0.5) * this.tileSize;
    this.sprite.y = (tileY + 1.0) * this.tileSize;

    const targetX = (target.x + 0.5) * this.tileSize;
    const targetY = (target.y + 1.0) * this.tileSize;
    const startX = this.sprite.x;
    const startY = this.sprite.y;

    // Walk-frame alternation: increment counter so updateSpriteFrame
    // picks walk-A vs walk-B (frames 3↔4 / 5↔6 / 7↔8). Applies to
    // both step + hop — the hop's "in motion" pose reads better with
    // a leg-extended frame than the idle pose.
    this.stepCounter++;
    // Stage walk state for updateWalkProgress to consume each frame
    // from WorldScene.advanceStep. Both step and hop are progress-
    // driven (no parallel Phaser tween) so the follower finishes its
    // animation in lockstep with the player's step — no snap on
    // chain-step-after-hop (Bug 6).
    this.walkActive = true;
    this.walkKind = kind;
    this.walkStartX = startX;
    this.walkStartY = startY;
    this.walkTargetX = targetX;
    this.walkTargetY = targetY;
    this.updateSpriteFrame(); // walk-frame for the current facing
  }

  /**
   * Lerp the follower's sprite position in lock-step with the
   * player's step progress (0.0 → 1.0). Called every frame by
   * WorldScene.advanceStep so the follower never drifts behind a
   * parallel timing source. No-op when walkActive is false (idle,
   * turn-in-place, hop, send-out, etc.).
   */
  updateWalkProgress(t: number): void {
    if (!this.walkActive || !this.sprite) return;
    const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
    this.sprite.x = this.walkStartX + (this.walkTargetX - this.walkStartX) * clamped;
    let y = this.walkStartY + (this.walkTargetY - this.walkStartY) * clamped;
    // Sin-arc lift for the hop kind — peaks at midpoint, returns to
    // the baseline at t=1. Subtracted because Phaser y grows downward.
    if (this.walkKind === 'hop') {
      y -= Math.sin(clamped * Math.PI) * HOP_ARC_PX;
    }
    this.sprite.y = y;
  }

  /** Run the 4-stage Pokéball-pop send-out. `deferredMaterializeSpecies`
   *  is set by `onLeadChanged` so the texture swap happens during
   *  stage 1's onComplete (after the old sprite faded to alpha 0),
   *  not before — keeps the old species visible during the fade-out
   *  beat and the new species hidden until it emerges from the ball
   *  in stage 4. Other callers (spawn / map-load / wake) leave it
   *  undefined and materialize themselves before calling. */
  private beginSendOut(deferredMaterializeSpecies?: string): void {
    if (!this.sprite || !this.pokeball || !this.flash) return;
    if (!this.state.snapshot().sendOutInProgress) return;
    this.cancelSendOut();

    const { tileX, tileY } = this.state.snapshot();
    const px = (tileX + 0.5) * this.tileSize;
    const py = (tileY + 1.0) * this.tileSize;

    this.pokeball.setPosition(px, py).setAlpha(0).setScale(0).setVisible(true);
    this.flash.setPosition(px, py).setAlpha(0).setScale(0.5).setVisible(false);

    this.sendOutTweens.push(
      this.scene.tweens.add({
        targets: this.sprite,
        alpha: 0,
        duration: SEND_OUT_STAGE_OLD_FADE_MS,
        ease: 'Linear',
        onComplete: () => {
          // Sprite is now invisible. If the caller deferred the
          // texture swap (lead-change), do it here so the old
          // species was the one being faded.
          if (deferredMaterializeSpecies) {
            this.materialize(deferredMaterializeSpecies);
            // materialize may have created a new sprite (lazy path);
            // re-apply the hidden state.
            this.sprite?.setAlpha(0);
            this.applyVisibility();
            this.snapToHomeTile();
          }
          this.sprite?.setScale(0);
        },
      }),
    );

    this.sendOutTimers.push(
      this.scene.time.delayedCall(SEND_OUT_TIMER_POKEBALL_AT_MS, () => {
        if (!this.state.snapshot().sendOutInProgress) return;
        if (!this.pokeball) return;
        void audio.playSfx('ball_open');
        this.sendOutTweens.push(
          this.scene.tweens.add({
            targets: this.pokeball,
            alpha: 1,
            scale: 1,
            duration: SEND_OUT_STAGE_POKEBALL_DURATION_MS,
            ease: 'Cubic.Out',
          }),
        );
      }),
    );

    this.sendOutTimers.push(
      this.scene.time.delayedCall(SEND_OUT_TIMER_FLASH_AT_MS, () => {
        if (!this.state.snapshot().sendOutInProgress) return;
        if (this.flash) {
          this.flash.setVisible(true).setAlpha(0).setScale(0.5);
          this.sendOutTweens.push(
            this.scene.tweens.add({
              targets: this.flash,
              alpha: 0.9,
              scale: 1.4,
              duration: SEND_OUT_STAGE_FLASH_DURATION_MS / 2,
              yoyo: true,
              ease: 'Cubic.Out',
              onComplete: () => this.flash?.setVisible(false),
            }),
          );
        }
        if (this.pokeball) {
          this.sendOutTweens.push(
            this.scene.tweens.add({
              targets: this.pokeball,
              alpha: 0,
              duration: SEND_OUT_STAGE_FLASH_DURATION_MS / 2,
              ease: 'Linear',
              onComplete: () => this.pokeball?.setVisible(false),
            }),
          );
        }
      }),
    );

    this.sendOutTimers.push(
      this.scene.time.delayedCall(SEND_OUT_TIMER_EMERGE_AT_MS, () => {
        if (!this.state.snapshot().sendOutInProgress) return;
        if (!this.sprite) return;
        this.sprite.setAlpha(0).setScale(0);
        this.sendOutTweens.push(
          this.scene.tweens.add({
            targets: this.sprite,
            alpha: 1,
            scale: 1,
            duration: SEND_OUT_STAGE_EMERGE_DURATION_MS,
            ease: 'Cubic.Out',
            onComplete: () => {
              this.state.completeSendOut();
            },
          }),
        );
      }),
    );
  }

  private cancelWalkTween(): void {
    // Both step and hop are progress-driven; clearing walkActive is
    // enough — no parallel Phaser tween to stop.
    this.walkActive = false;
  }

  private cancelSendOut(): void {
    for (const t of this.sendOutTweens) {
      if (t.isPlaying() || t.isPaused()) t.stop();
    }
    this.sendOutTweens = [];
    for (const timer of this.sendOutTimers) {
      if (!timer.hasDispatched) timer.destroy();
    }
    this.sendOutTimers = [];
    this.pokeball?.setVisible(false).setAlpha(0).setScale(0);
    this.flash?.setVisible(false).setAlpha(0).setScale(0.5);
    if (this.sprite) {
      this.sprite.setAlpha(1).setScale(1);
    }
  }

  /** Ensure the species' OW spritesheet is loaded. v0.1 species are
   *  all preloaded by WorldScene → this resolves immediately. Other
   *  species trigger Phaser's loader at runtime. */
  private async ensureLoaded(species: string): Promise<void> {
    const textureKey = `follower-ow:${species}`;
    if (this.scene.textures.exists(textureKey)) return;
    if (this.lazyLoadStarted.has(species)) return; // in-flight; return early

    const dexId = SPECIES_TO_DEX[species];
    if (!dexId) {
      console.warn(`[follower] unknown species (no dex id): ${species}`);
      return;
    }

    this.lazyLoadStarted.add(species);
    await new Promise<void>((resolve) => {
      this.scene.load.once('complete', () => resolve());
      this.scene.load.spritesheet(
        textureKey,
        `assets/pokemon/follower/${dexId}.png`,
        { frameWidth: 32, frameHeight: 32 },
      );
      this.scene.load.start();
      console.log(`[follower] lazy-loading ${species}`);
    });
  }
}
